import { el, clear } from "./dom";
import { t } from "@bn-games/i18n-core";

export interface ReplayControlsCallbacks {
  readonly onFirst: () => void;
  readonly onPrev: () => void;
  readonly onPlayPause: () => void;
  readonly onNext: () => void;
  readonly onLast: () => void;
  readonly onSpeed: (speed: number) => void;
  readonly onBack: () => void;
}

export interface ReplayControlsState {
  readonly stepIndex: number;
  readonly totalSteps: number;
  readonly speed: number;
  readonly isPlaying: boolean;
  readonly backLabel: string;
}

const SPEEDS: readonly number[] = [0.5, 1, 2, 4];

export class ReplayControls {
  private host: HTMLElement | null = null;
  private readonly callbacks: ReplayControlsCallbacks;
  private state: ReplayControlsState;

  constructor(callbacks: ReplayControlsCallbacks, initial: ReplayControlsState) {
    this.callbacks = callbacks;
    this.state = initial;
  }

  mount(): void {
    if (this.host) return;
    const host = el("div", { class: "replay-controls" });
    this.host = host;
    document.body.appendChild(host);
    this.render();
  }

  unmount(): void {
    if (!this.host) return;
    this.host.remove();
    this.host = null;
  }

  update(state: ReplayControlsState): void {
    this.state = state;
    this.render();
  }

  private render(): void {
    if (!this.host) return;
    clear(this.host);

    const counter = el("div", {
      class: "replay-controls__counter",
      text: `${this.state.stepIndex} / ${this.state.totalSteps}`,
    });

    const atStart = this.state.stepIndex === 0;
    const atEnd = this.state.stepIndex >= this.state.totalSteps;
    const transport = el("div", { class: "replay-controls__transport" }, [
      this.makeBtn("⏮", t("replay.controls.first"), atStart, this.callbacks.onFirst),
      this.makeBtn("◀", t("replay.controls.previous"), atStart, this.callbacks.onPrev),
      this.makeBtn(
        this.state.isPlaying ? "⏸" : "▶",
        this.state.isPlaying ? t("replay.controls.pause") : t("replay.controls.play"),
        atEnd,
        this.callbacks.onPlayPause,
        true,
      ),
      this.makeBtn("▶", t("replay.controls.next"), atEnd, this.callbacks.onNext),
      this.makeBtn("⏭", t("replay.controls.last"), atEnd, this.callbacks.onLast),
    ]);

    const speedNode = el("div", { class: "replay-controls__speed" }, [
      el("span", { class: "replay-controls__speed-label", text: t("replay.controls.speed") }),
      ...SPEEDS.map((s) => {
        const cls = s === this.state.speed
          ? "replay-controls__speed-btn replay-controls__speed-btn--active"
          : "replay-controls__speed-btn";
        return el("button", {
          class: cls,
          attrs: { type: "button" },
          text: `${s}×`,
          on: { click: () => this.callbacks.onSpeed(s) },
        });
      }),
    ]);

    const back = el("button", {
      class: "replay-controls__back",
      attrs: { type: "button" },
      text: this.state.backLabel,
      on: { click: () => this.callbacks.onBack() },
    });

    const left = el("div", { class: "replay-controls__group" }, [back]);
    const center = el("div", { class: "replay-controls__group replay-controls__group--center" }, [
      transport,
      counter,
    ]);
    const right = el("div", { class: "replay-controls__group replay-controls__group--right" }, [speedNode]);

    this.host.appendChild(left);
    this.host.appendChild(center);
    this.host.appendChild(right);
  }

  private makeBtn(
    glyph: string,
    label: string,
    disabled: boolean,
    onClick: () => void,
    primary = false,
  ): HTMLElement {
    const cls = primary
      ? "replay-controls__btn replay-controls__btn--primary"
      : "replay-controls__btn";
    const btn = el("button", {
      class: cls,
      attrs: { type: "button", "aria-label": label, title: label },
      text: glyph,
      on: { click: () => onClick() },
    });
    if (disabled) btn.setAttribute("disabled", "true");
    return btn;
  }
}
